/* eslint-disable complexity */
import { useEffect, useMemo } from "react";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import {
  selectCheckoutCart,
  selectDeliveryModeDetails,
  selectIsSending,
  selectTotalCheckoutValue,
  selectTotalWeight,
} from "~/features/cart/cart/redux/selectors/checkoutPageSelectors";
import { useDispatch, useSelector } from "react-redux";
import { userIsInquirer, userIsInternalCustomer } from "~/domains/user/utils/roles";
import { type RootState } from "~/app/store";
import { PackageEntrySource } from "~/features/cart/cart/models/CartData";
import Modal from "~/common/components/Modal";
import { useBooleanState } from "~/common/hooks/useBooleanState";

import { OrderSummary } from "~/features/cart/cartV2/OrderSummary";
import { CheckoutFormSaver, useCheckoutLogic } from "~/features/cart/cart/useCheckoutLogic";
import { FormProvider } from "react-hook-form";
import {
  selectPackageEntryMap,
  selectTotalCartPriceWithoutDiscount,
} from "~/features/cart/cart/redux/selectors/cartSelectors";
import { type DeliveryOptionItem } from "./DeliveryOption";
import { Box, Button, Stack } from "~/common/mui";
import ToastMessage from "~/common/components/Messages/ToastMessage/ToastMessage";
import { trackCheckoutLoadThunk } from "~/features/cart/cart/redux/actions/checkoutPageActions";
import { useTranslation } from "react-i18next";
import { openModal } from "~/common/utils/ModalService";
import { CantQuoteSalesCampaignModal } from "~/features/salesCampaign/components/dialogs/CantQuoteSalesCampaignModal/CantQuoteSalesCampaignModal";
import { ErrorCode } from "~/features/cart/cart/api/placeOrder";
import { SoldOutCampaignModal } from "~/features/salesCampaign/components/dialogs/SoldOutCampaignModal/SoldOutCampaignModal";
import ProductTableV2 from "~/features/cart/cartV2/ProductTable";

import {
  selectCartContainsSurcharge,
  selectCartContainsTariffSurcharge,
  selectCartSurcharge,
} from "~/features/cart/cart/redux/selectors/selectors";
import { SendingType } from "~/features/cart/cart/redux/types";
import CheckoutSummary from "./CheckoutSummary/CheckoutSummary";
import { CartSectionTitle } from "./CartSectionTitle";

export interface CheckoutForm {
  poNumber: string;
  orderComment: string;
  deliveryCode: string;
  completeDelivery: DeliveryOptionItem;

  optime?: CheckoutFormOptime;
  paymentType: string;
  creditCardID: string;
  freightPaymentCode: string;
  freightPaymentAccountNumber: string;
  orderType: string;
}

export interface CheckoutFormOptime {
  consent: boolean;
  email: string;
  name: string;
  lastName: string;
  plant: string;
  timeZone: string;
  language: string;
  country: string;
}

export interface FreightPaymentItem {
  type: string;
  accountNumber: boolean;
}

const TOAST_MESSAGES: Record<ErrorCode, string> = {
  [ErrorCode.Generic]: "checkout.placeOrder.failed",
  [ErrorCode.CreditCard]: "checkout.placeOrder.payment.failed",
  [ErrorCode.SaleStock]: "",
};

const CheckoutCart = () => {
  const { t } = useTranslation();

  const webservicesDisabled = window.SCHA.config.disableAllWS;

  const { any: hasSomeSalesCampaignItems, all: hasOnlySalesCampaignItems } = useSelector(
    selectContainsSalesCampaignItems,
  );

  const cartData = useSelector(selectCheckoutCart);

  const containsQuoteItemsInQuotables = useSelector(selectContainsQuoteInQuotables);
  const containsQuoteItemsAtAll = useSelector(selectContainsQuoteItemsAtAll);

  const { handleSubmitClick, formProps, placeOrderError, resetPlaceOrderError } = useCheckoutLogic();

  const { watch, setValue } = formProps;

  const deliveryCode = watch("deliveryCode");
  const methodPayment = watch("paymentType");
  const orderTypeState = watch("orderType");
  const deliveryDetails = useSelector(selectDeliveryModeDetails(deliveryCode, methodPayment));
  const expressDelivery = "EXPRESS";

  let extraElements = undefined;
  if (deliveryDetails && deliveryDetails?.paymentCodeDisplay) {
    const shipmentMethodLabel = t(`data.shipmentMethod.${deliveryDetails.code}`);

    extraElements = [
      {
        label: t("checkout.shipmentInfo.label", {
          context: window.SCHA.config.translationCountryCode,
          shipmentType: shipmentMethodLabel,
        }),
        value:
          deliveryDetails.code === expressDelivery
            ? t(`data.paymentCodeDisplay.EXPRESS`, {
                context: window.SCHA.config.translationCountryCode,
              })
            : undefined,
      },
    ];
  }

  const totalWeight = useSelector(selectTotalWeight);
  const totalSum = useSelector(selectTotalCheckoutValue);
  const totalDiscount = useSelector(selectTotalCartPriceWithoutDiscount);

  const hasCartElement =
    cartData.orderables.entries.length > 0 || cartData.bundles.some((bundle) => bundle.entries.length > 0);
  const hasQuoteElement = cartData.quotables.entries.length > 0;

  const displayOrderableItemsSection = cartData.orderables.entries.length > 0;

  const sending = useSelector(selectIsSending);

  const hasAnySurcharge = useSelector(selectCartContainsSurcharge);
  const hasAnyTariffSurcharge = useSelector(selectCartContainsTariffSurcharge);

  const surchargeValue = useSelector(selectCartSurcharge);

  const {
    val: showQuoteItemsFromQuoteInfo,
    setTrue: openShowQuoteItemsFromQuoteInfo,
    setFalse: closeShowQuoteItemsFromQuoteInfo,
  } = useBooleanState(false);

  const dispatch = useDispatch();

  const disablePlaceQuote =
    webservicesDisabled ||
    !userHasPermission(Permission.PLACE_QUOTE) ||
    hasOnlySalesCampaignItems ||
    sending === SendingType.ORDER;
  const disablePlaceOrder =
    webservicesDisabled ||
    !userHasPermission(Permission.PLACE_ORDER) ||
    userIsInquirer() ||
    userIsInternalCustomer() ||
    sending === SendingType.QUOTE;

  const bothButtonsDisabled = disablePlaceOrder && disablePlaceQuote;

  const actionButtons = useMemo(() => {
    const mixedCart = true;

    const actions = [];

    if (hasCartElement) {
      actions.push(
        <Button
          key={"placeOrder"}
          data-meta={"checkout"}
          data-test-id={hasQuoteElement ? "btn-place-mixed" : "btn-place-order"}
          className={"f-no-wrap"}
          fullWidth={true}
          size="large"
          disabled={disablePlaceOrder}
          loading={sending === SendingType.ORDER}
          onClickCapture={() => {
            const paymentType = formProps.getValues("paymentType");
            dispatch(trackCheckoutLoadThunk(paymentType));
          }}
          onClick={() => {
            if (containsQuoteItemsInQuotables) {
              openShowQuoteItemsFromQuoteInfo();
            } else {
              handleSubmitClick(false);
            }
          }}
        >
          {hasQuoteElement ? t("page.checkout.btn.mixedOrder") : t("checkoutPage.btn.order")}
        </Button>,
      );
    }

    actions.push(
      <Button
        key={"placeQuote"}
        data-meta={"checkout"}
        data-test-id={"btn-place-quote"}
        color={hasCartElement ? "secondary" : "primary"}
        className={"f-no-wrap"}
        fullWidth={true}
        size="large"
        disabled={disablePlaceQuote}
        loading={sending === SendingType.QUOTE}
        onClickCapture={() => {
          const paymentType = formProps.getValues("paymentType");
          dispatch(trackCheckoutLoadThunk(paymentType));
        }}
        onClick={() => {
          if (containsQuoteItemsAtAll) {
            openShowQuoteItemsFromQuoteInfo();
          } else if (hasSomeSalesCampaignItems) {
            openModal("CantQuoteSalesCampaignModal", (onClose) => (
              <CantQuoteSalesCampaignModal
                onClose={onClose}
                disablePlaceOrder={disablePlaceOrder}
                onPlaceOrder={() => handleSubmitClick(false)}
              />
            ));
          } else {
            handleSubmitClick(true);
          }
        }}
      >
        {mixedCart ? t("page.checkout.btnDescription.quote") : t("checkoutPage.btn.quote")}
      </Button>,
    );

    return actions;
  }, [sending, t, handleSubmitClick, hasQuoteElement, hasCartElement]);

  const cartProductsList = [
    ...cartData.orderables.entries,
    ...cartData.quotables.entries,
    ...cartData.bundles.flatMap((bundle) => bundle.entries),
  ];

  const packageEntryMap = useSelector(selectPackageEntryMap);

  // create a list of all available products in checkout
  // extract for each product user's delivery date
  const cartProductsDeliveryDateList: Date[] = cartProductsList
    .flatMap((groupingEntry) => groupingEntry.packGroups)
    .flatMap((packGroup) => packGroup.packEntryIds)
    .map((packId) => packageEntryMap[packId])
    .filter((packData) => packData.edd && packData.edd.date !== null && packData.edd.date !== undefined)
    .map((packData) => packData.edd?.date) as Date[];

  const selectedOrderType = window.SCHA.config.selectedOrderType;

  useEffect(() => {
    if (!orderTypeState && !!selectedOrderType) {
      setValue("orderType", selectedOrderType);
    }
  }, []);

  const showToast = placeOrderError && [ErrorCode.Generic, ErrorCode.CreditCard].includes(placeOrderError);

  useEffect(() => {
    if (placeOrderError === ErrorCode.SaleStock) {
      openModal("OutOfStockModal", (onClose) => (
        <SoldOutCampaignModal
          onClose={() => {
            onClose();
            resetPlaceOrderError();
          }}
        />
      ));
    }
  }, [placeOrderError]);

  return (
    <>
      {showToast && <ToastMessage type={"error"}>{t(TOAST_MESSAGES[placeOrderError])}</ToastMessage>}
      <FormProvider {...formProps}>
        <CheckoutFormSaver />
        <CheckoutSummary
          actionButtons={actionButtons}
          cartProductsDeliveryDateList={cartProductsDeliveryDateList}
          surcharge={hasAnySurcharge && totalSum && totalSum.value > 0 ? surchargeValue : undefined}
          tariffSurcharge={hasAnyTariffSurcharge}
          bothButtonsDisabled={bothButtonsDisabled}
          totalDiscount={totalDiscount}
        />

        <Stack gap={3}>
          {displayOrderableItemsSection && (
            <>
              <CartSectionTitle
                title={t("cartPage.header.toBeDelivered", { count: cartData.orderables.entries.length })}
                editCart
              />
              <ProductTableV2
                data-test-id={"checkout-section-orderable"}
                group={cartData.orderables}
                editMode={false}
              />
            </>
          )}

          {cartData.bundles.map((bundle) => (
            <>
              <CartSectionTitle
                title={t("page.cart.bundledQuote.title", {
                  amount: bundle.entries.length,
                  quoteNumber: bundle.quoteNumber,
                })}
                subtitle={t("page.cart.bundledQuote.subtitle")}
                editCart
              />
              <ProductTableV2 data-test-id={`checkout-section-bundle-${bundle.quoteNumber}`} group={bundle} />
            </>
          ))}

          {hasQuoteElement && (
            <>
              <CartSectionTitle
                title={t("cartPage.header.toBeQuoted", { count: cartData.quotables.entries.length })}
                editCart
              />
              <ProductTableV2 data-test-id={"checkout-section-quotable"} group={cartData.quotables} editMode={false} />
            </>
          )}
        </Stack>

        <Box sx={{ display: "flex", flexFlow: "column", alignItems: "flex-end", my: 4 }}>
          <OrderSummary sx={{ justifySelf: "flex-end" }}>
            <OrderSummary.DataContainer>
              <OrderSummary.WeightRow weight={totalWeight} />
              <OrderSummary.SurchargeRow
                surcharge={hasAnySurcharge && totalSum && totalSum.value > 0 ? surchargeValue : undefined}
              />
              <OrderSummary.DiscountRow discount={totalDiscount} />
              <OrderSummary.TotalRow
                value={webservicesDisabled ? null : totalSum}
                showPriceOnRequest
                asterisk={hasAnySurcharge || hasAnyTariffSurcharge}
              />
              <OrderSummary.TaxDisclaimer />
              {extraElements &&
                extraElements.map((row, i) => <OrderSummary.Row key={i} label={row.label} value={row.value} />)}
            </OrderSummary.DataContainer>
            <OrderSummary.Divider />
            <OrderSummary.Actions>{actionButtons}</OrderSummary.Actions>
          </OrderSummary>
        </Box>

        {showQuoteItemsFromQuoteInfo && (
          <Modal onClose={closeShowQuoteItemsFromQuoteInfo}>{t("page.checkout.message.alreadyQuotedItems")}</Modal>
        )}
      </FormProvider>
    </>
  );
};

export default CheckoutCart;

const selectContainsQuoteInQuotables = (state: RootState) => {
  const quotables = selectCheckoutCart(state).quotables;

  if (quotables.entries.length === 0) {
    return false;
  }

  const packEntryMap = state.cart.packEntriesById;

  return quotables.entries
    .flatMap((entry) => entry.packGroups)
    .flatMap(($) => $.packEntryIds)
    .some((packEntryId) => packEntryMap[packEntryId].source === PackageEntrySource.QUOTE);
};

const selectContainsQuoteItemsAtAll = (state: RootState) => {
  return Object.values(state.cart.packEntriesById).some((packEntry) => packEntry.source === PackageEntrySource.QUOTE);
};

const selectContainsSalesCampaignItems = (state: RootState) => {
  let any = false;
  let all = true;
  Object.values(state.cart.packEntriesById).forEach((packEntry) => {
    any = any || !!packEntry.saleCampaignEnabled;
    all = all && !!packEntry.saleCampaignEnabled;
  });
  return {
    any,
    all,
  };
};
